"use client";

/**
 * 5-star feedback widget at the bottom of every blog post.
 *
 * Comment-input gate (per product spec):
 *   - 1 / 2 stars  -> textarea: "What didn't you like?"
 *   - 3 / 4 stars  -> no textarea (middle-of-the-road; we don't pester)
 *   - 5 stars      -> textarea: "What did you like that much?"
 *
 * Identity: signed-in users dedupe on user_id, anon on the
 * X-Fingerprint header that lib/api already attaches to every
 * request. Re-submitting against the same post upserts the row, so
 * a reader can change their mind. We pre-fill the previous rating
 * + comment on mount via /feedback/me so a re-visit shows what they
 * said before.
 *
 * "Submit" is the explicit confirm action - clicking a star alone
 * doesn't fire the network call. That keeps the widget feeling
 * deliberate and lets the user see the comment prompt before
 * committing.
 */
import { useEffect, useRef, useState } from "react";
import { Star, Check } from "lucide-react";

import { useT, useLocale } from "@/lib/locale-context";
import { submitBlogFeedback, getMyBlogFeedback } from "@/lib/api";

interface Props {
  slug: string;
}

const COMMENT_RATINGS = new Set([1, 2, 5]);

export default function BlogFeedback({ slug }: Props) {
  const t = useT();
  const { locale } = useLocale();

  const [rating, setRating] = useState<number>(0);     // 0 = unselected
  const [hover, setHover] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from a prior submission so a re-visit shows the
  // existing rating instead of the unselected state. Only once the
  // widget nears the viewport: the lookup needs the browser
  // fingerprint, and computing that during page load cost a phone
  // 250-700 ms of main thread for a widget at the end of the post.
  const rootRef = useRef<HTMLElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (near || !el) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setNear(true); },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    getMyBlogFeedback(slug)
      .then((res) => {
        if (cancelled) return;
        if (res.rating != null) {
          setRating(res.rating);
          setComment(res.comment ?? "");
          setDone(true);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [slug, near]);

  const wantsComment = COMMENT_RATINGS.has(rating);
  const commentLabel: string = (() => {
    if (rating === 5) return t("blog.feedback.comment.high");
    if (rating === 1 || rating === 2) return t("blog.feedback.comment.low");
    return "";
  })();

  const onSubmit = async () => {
    if (rating < 1 || rating > 5 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitBlogFeedback(slug, {
        rating,
        locale,
        comment: wantsComment && comment.trim() ? comment.trim() : null,
      });
      setDone(true);
    } catch {
      setError(t("blog.feedback.error"));
    } finally {
      setSubmitting(false);
    }
  };

  // After a successful submit the widget collapses to a thank-you
  // line. Clicking a different star re-opens the editor so the user
  // can change their rating without reloading the page.
  if (done) {
    return (
      <section ref={rootRef} className="mt-12 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
            <Check className="w-4.5 h-4.5 text-emerald-700" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">
              {t("blog.feedback.thanks")}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {t("blog.feedback.your_rating").replace("{n}", String(rating))}
            </p>
            <button
              type="button"
              onClick={() => setDone(false)}
              className="mt-2 text-xs font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900"
            >
              {t("blog.feedback.change")}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={rootRef} className="mt-12 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
      <h3 className="text-base sm:text-lg font-bold text-gray-900">
        {t("blog.feedback.title")}
      </h3>
      <p className="mt-1 text-sm text-gray-600">
        {t("blog.feedback.subtitle")}
      </p>

      <div className="mt-4 flex items-center gap-1.5" role="radiogroup" aria-label={t("blog.feedback.title")}>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = (hover || rating) >= n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={String(n)}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="p-1 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Star
                className={`w-7 h-7 transition-colors ${
                  active ? "text-amber-400 fill-amber-400" : "text-gray-300"
                }`}
              />
            </button>
          );
        })}
      </div>

      {wantsComment && (
        <div className="mt-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            {commentLabel}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={1000}
            className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder={t("blog.feedback.comment.placeholder")}
          />
        </div>
      )}

      {error && (
        <p className="mt-3 text-xs text-rose-600">{error}</p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={rating < 1 || submitting}
          onClick={onSubmit}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? t("blog.feedback.submitting") : t("blog.feedback.submit")}
        </button>
      </div>
    </section>
  );
}
